import dayjs from "dayjs";
import { useCallback, useEffect, useRef, useState } from "react";
import { DateRange, DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";

import { Icon } from "@web-app/components";

interface DateRangeDropdownProps {
  dateRange: {
    from: Date;
    to: Date;
  };
  onChange: (dateRange: { from: Date; to: Date }) => void;
}

const DATE_PRESETS = [
  {
    getValue: () => ({
      from: dayjs().startOf("day").toDate(),
      to: dayjs().endOf("day").toDate(),
    }),
    label: "Today",
  },
  {
    getValue: () => ({
      from: dayjs().subtract(1, "day").startOf("day").toDate(),
      to: dayjs().subtract(1, "day").endOf("day").toDate(),
    }),
    label: "Yesterday",
  },
  {
    getValue: () => ({
      from: dayjs().startOf("week").toDate(),
      to: dayjs().endOf("week").toDate(),
    }),
    label: "This Week",
  },
  {
    getValue: () => ({
      from: dayjs().subtract(1, "week").startOf("week").toDate(),
      to: dayjs().subtract(1, "week").endOf("week").toDate(),
    }),
    label: "Last Week",
  },
  {
    getValue: () => ({
      from: dayjs().startOf("month").toDate(),
      to: dayjs().endOf("month").toDate(),
    }),
    label: "This Month",
  },
  {
    getValue: () => ({
      from: dayjs().subtract(1, "month").startOf("month").toDate(),
      to: dayjs().subtract(1, "month").endOf("month").toDate(),
    }),
    label: "Last Month",
  },
  {
    getValue: () => ({
      from: dayjs().startOf("year").toDate(),
      to: dayjs().endOf("year").toDate(),
    }),
    label: "This Year",
  },
  {
    getValue: () => ({
      from: dayjs().subtract(6, "days").startOf("day").toDate(),
      to: dayjs().endOf("day").toDate(),
    }),
    label: "Last 7 Days",
  },
  {
    getValue: () => ({
      from: dayjs().subtract(29, "days").startOf("day").toDate(),
      to: dayjs().endOf("day").toDate(),
    }),
    label: "Last 30 Days",
  },
];

export default function DateRangeDropdown({
  dateRange,
  onChange,
}: DateRangeDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const formatDateRange = () => {
    const fromStr = dayjs(dateRange.from).format("MMM D");
    const toStr = dayjs(dateRange.to).format("MMM D, YYYY");
    return `${fromStr} - ${toStr}`;
  };

  const close = useCallback(() => setIsOpen(false), []);

  // Close on outside click using capture phase to beat Dialog's handler
  useEffect(() => {
    if (!isOpen) return;

    const handlePointerDown = (e: PointerEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("pointerdown", handlePointerDown, true);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown, true);
    };
  }, [isOpen]);

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className="flex items-center gap-2 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-700 border border-gray-300 transition ease-in hover:bg-gray-50 focus:outline-none"
      >
        <Icon name="fasCalendarDays" className="h-4 w-4" />
        <span>{formatDateRange()}</span>
        <Icon name="fasChevronDown" className="h-3 w-3" />
      </button>

      {isOpen && (
        <div className="absolute left-0 top-full z-[60] mt-1 rounded-md bg-white shadow-lg border border-gray-200">
          <div className="flex">
            {/* Preset shortcuts */}
            <div className="border-r border-gray-200 p-2 min-w-[140px]">
              <div className="text-xs font-medium text-gray-500 px-2 py-1.5">
                Quick Select
              </div>
              <div className="space-y-0.5">
                {DATE_PRESETS.map((preset) => (
                  <button
                    key={preset.label}
                    type="button"
                    onClick={() => {
                      onChange(preset.getValue());
                      close();
                    }}
                    className="w-full text-left px-2 py-1.5 text-sm text-gray-700 hover:bg-gray-100 rounded transition-colors"
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Calendar picker */}
            <div className="p-4 pt-1">
              <DayPicker
                mode="range"
                selected={{
                  from: dateRange.from,
                  to: dateRange.to,
                }}
                onSelect={(range: DateRange | undefined) => {
                  if (range?.from && range?.to) {
                    onChange({
                      from: dayjs(range.from).startOf("day").toDate(),
                      to: dayjs(range.to).endOf("day").toDate(),
                    });
                  }
                }}
                className="rdp-custom"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
